/**
 * @file Integrated functionality for jobs and quotes
 * Provides strongly-typed functions that integrate job and quote operations
 */

import { supabase } from "@/integrations/supabase/client";
import { UserJob, Quote } from "@/lib/types";
import { handleApiError, withErrorHandling } from "@/lib/errors";
import { requestQuote } from "@/lib/supabase/quotes";
import { 
  CreateJobWithQuotesParams,
  JobWithQuotesResponse 
} from "@/lib/types/quote-types";
import { debug } from "@/lib/utils";

/**
 * Creates a custom job and associated quote requests
 * This function handles the full flow of creating a job and sending quote requests
 *
 * @param userId The ID of the user creating the job
 * @param params Job creation parameters including quote recipients
 * @returns Object containing the created job and associated quotes
 */
export const createJobWithQuotes = withErrorHandling(
  async (
    userId: string,
    params: CreateJobWithQuotesParams
  ): Promise<JobWithQuotesResponse> => {
    // Validate parameters
    if (!params.title) {
      throw new Error('Job title is required');
    }
    
    if (!params.description) {
      throw new Error('Job description is required');
    }
    
    if (!params.trade_ids || params.trade_ids.length === 0) {
      throw new Error('At least one tradesperson must be selected');
    }
    
    // 1. Create the custom job
    const { data: job, error: jobError } = await supabase
      .from("user_jobs")
      .insert([{
        user_id: userId,
        title: params.title,
        description: params.description,
        category: params.category,
        status: 'quote_requested',
        trade_id: params.primary_trade_id, // Optional and may be null/undefined
        is_custom: true,
        created_at: new Date().toISOString()
      }])
      .select(`
        *,
        tradesperson:trade_id(*)
      `)
      .single();
    
    if (jobError) throw jobError;
    
    // 2. Create quote requests for all selected tradespeople
    const quotePromises = params.trade_ids.map(tradeId => 
      requestQuote(userId, {
        job_id: job.id,
        trade_id: tradeId
      })
    );
    
    // Filter out any failed quote requests (null results)
    const quotesWithNulls = await Promise.all(quotePromises);
    const quotes = quotesWithNulls.filter((q): q is Quote => q !== null);
    
    // Log if any quote requests failed
    if (quotes.length < params.trade_ids.length) {
      debug.warn(`Some quote requests failed: ${quotes.length} of ${params.trade_ids.length} succeeded`);
    }
    
    return { job, quotes };
  },
  null, // Return null on error
  'Failed to create job with quotes'
);

/**
 * Get job statistics with quote information
 * Useful for dashboards showing job and quote performance metrics
 * 
 * @param userId The ID of the user
 * @returns Statistics about jobs and their associated quotes
 */
export const getJobQuoteStatistics = withErrorHandling(
  async (userId: string): Promise<{
    totalJobs: number;
    quoteRequestedJobs: number;
    completedJobs: number;
    totalQuotes: number;
    pricedQuotes: number;
    declinedQuotes: number;
    pendingQuotes: number;
    acceptedQuotes: number;
    averageQuotesPerJob: number;
  }> => {
    // Get all jobs
    const { data: jobs, error: jobsError } = await supabase
      .from("user_jobs")
      .select("id, status")
      .eq("user_id", userId);
      
    if (jobsError) throw jobsError;
    
    // Get all quotes
    const { data: quotes, error: quotesError } = await supabase
      .from("quotes")
      .select("id, phase, job_id")
      .eq("user_id", userId);
      
    if (quotesError) throw quotesError;
    
    // Calculate statistics
    const totalJobs = jobs.length;
    const quoteRequestedJobs = jobs.filter(job => job.status === 'quote_requested').length;
    const completedJobs = jobs.filter(job => job.status === 'completed').length;
    
    const totalQuotes = quotes.length;
    const pricedQuotes = quotes.filter(quote => quote.phase === 'priced').length;
    const declinedQuotes = quotes.filter(quote => quote.phase === 'declined').length;
    const pendingQuotes = quotes.filter(quote => 
      quote.phase === 'requested' || quote.phase === 'visit_booked'
    ).length;
    
    // Count accepted quotes (where a job has status 'scheduled' or 'completed' and has a quote)
    const scheduledJobIds = new Set(
      jobs.filter(job => job.status === 'scheduled' || job.status === 'completed')
          .map(job => job.id)
    );
    
    const acceptedQuotes = quotes.filter(quote => 
      quote.job_id && scheduledJobIds.has(quote.job_id)
    ).length;
    
    // Calculate averages
    const jobsWithQuotes = new Set(quotes.map(quote => quote.job_id)).size;
    const averageQuotesPerJob = jobsWithQuotes ? totalQuotes / jobsWithQuotes : 0;
    
    return {
      totalJobs,
      quoteRequestedJobs,
      completedJobs,
      totalQuotes,
      pricedQuotes,
      declinedQuotes,
      pendingQuotes,
      acceptedQuotes,
      averageQuotesPerJob,
    };
  },
  {
    totalJobs: 0,
    quoteRequestedJobs: 0,
    completedJobs: 0,
    totalQuotes: 0,
    pricedQuotes: 0,
    declinedQuotes: 0,
    pendingQuotes: 0,
    acceptedQuotes: 0,
    averageQuotesPerJob: 0,
  }, // Return empty stats on error
  'Failed to get job quote statistics'
);